
import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { paymentSchema, PaymentFormValues } from './types';
import PaymentMethodTabs from './PaymentMethodTabs';
import PaymentFormActions from './PaymentFormActions';

type PaymentReceivingFormProps = {
  onSave: (data: PaymentFormValues) => Promise<void>;
  initialData?: Partial<PaymentFormValues>;
};

const PaymentReceivingForm: React.FC<PaymentReceivingFormProps> = ({ 
  onSave, 
  initialData = {} 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("bank");
  const { toast } = useToast();
  
  const defaultValues: Partial<PaymentFormValues> = {
    accountHolderName: "",
    accountNumber: "",
    routingNumber: "",
    bankName: "",
    paypalEmail: "",
    paypalUsername: "",
    preferredMethod: "bank",
    ...initialData
  };
  
  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentSchema),
    defaultValues,
  });
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    form.setValue("preferredMethod", value as "bank" | "paypal");
  };
  
  const onSubmit = async (data: PaymentFormValues) => {
    setIsLoading(true);
    
    try {
      await onSave(data);
      toast({
        title: "Payment details saved",
        description: "Your payment information has been saved successfully.",
      });
    } catch (error) {
      console.error("Error saving payment details:", error);
      toast({
        title: "Failed to save payment details",
        description: "There was an error saving your payment information. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Card className="w-full overflow-hidden border-none shadow-lg bg-gradient-to-br from-white to-blue-50">
      <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50 pb-4">
        <CardTitle className="text-primary-blue text-xl flex items-center">
          Payment Receiving Methods
        </CardTitle>
        <CardDescription className="text-gray-600">
          Add your payment information to receive referral payments
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <PaymentMethodTabs 
              form={form} 
              activeTab={activeTab} 
              onTabChange={handleTabChange} 
            />
            
            <PaymentFormActions isLoading={isLoading} />
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default PaymentReceivingForm;
